import { useMemo, useState } from 'react';
import { Select, SegmentedControl, Stack } from '@mantine/core';
import { useLivestockList } from '@/hooks/useLivestock';
import { LIVESTOCK_TYPE_META } from './constants';
import type { LivestockType } from '@/types/livestock';

interface LivestockPickerProps {
  value: number | null;
  onChange: (id: number) => void;
  label?: string;
}

const STATUS_ORDER: Record<string, number> = {
  alive: 0,
  unknown: 1,
  sold: 2,
  deceased: 3,
};

const TYPE_ORDER: LivestockType[] = ['fish', 'coral', 'invertebrate', 'plant'];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...TYPE_ORDER.map((t) => ({ value: t, label: LIVESTOCK_TYPE_META[t].label })),
];

export function LivestockPicker({ value, onChange, label }: LivestockPickerProps) {
  const { data, isLoading } = useLivestockList();
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const options = useMemo(() => {
    if (!data?.livestock) return [];

    const filtered = typeFilter === 'all'
      ? data.livestock
      : data.livestock.filter((l) => l.livestock_type === typeFilter);

    // Sort: alive first within each type, types in standard order
    const sorted = [...filtered].sort((a, b) => {
      const typeA = TYPE_ORDER.indexOf(a.livestock_type);
      const typeB = TYPE_ORDER.indexOf(b.livestock_type);
      if (typeA !== typeB) return typeA - typeB;
      const statusA = STATUS_ORDER[a.status] ?? 99;
      const statusB = STATUS_ORDER[b.status] ?? 99;
      if (statusA !== statusB) return statusA - statusB;
      return a.common_name.localeCompare(b.common_name);
    });

    // When filtered to a single type, no need for group headers
    if (typeFilter !== 'all') {
      return sorted.map((item) => {
        const suffix = item.status !== 'alive' ? ` (${item.status})` : '';
        return { value: String(item.id), label: `${item.common_name}${suffix}` };
      });
    }

    // Group by type
    const groups: { group: string; items: { value: string; label: string }[] }[] = [];
    let currentType: string | null = null;

    for (const item of sorted) {
      if (item.livestock_type !== currentType) {
        currentType = item.livestock_type;
        const meta = LIVESTOCK_TYPE_META[currentType as LivestockType];
        groups.push({ group: meta?.label ?? currentType, items: [] });
      }
      const suffix = item.status !== 'alive' ? ` (${item.status})` : '';
      groups[groups.length - 1]!.items.push({
        value: String(item.id),
        label: `${item.common_name}${suffix}`,
      });
    }

    return groups;
  }, [data?.livestock, typeFilter]);

  return (
    <Stack gap="xs">
      <Select
        label={label ?? 'Assign to'}
        placeholder="Select organism..."
        data={options}
        value={value ? String(value) : null}
        onChange={(v) => v && onChange(Number(v))}
        searchable
        nothingFoundMessage="No livestock found"
        disabled={isLoading}
        maxDropdownHeight={280}
      />
      <SegmentedControl
        value={typeFilter}
        onChange={setTypeFilter}
        data={FILTER_OPTIONS}
        size="xs"
        fullWidth
      />
    </Stack>
  );
}
